import * as React from 'react';
import { useCallback, useContext } from 'react';
import styled from 'styled-components';

import { AccountInfoContext } from '../account/account-info';
import { InteractiveModal } from '../ui-elements/modals/InteractiveModal';
import { Button } from '../ui-elements/Main/Button';
import { colors } from '../ui-elements/Main/MainTheme';
import { usePhoneVerification, formatNANP, PhoneVerifyResult } from './use-phone-verification.hook';

export type TrialPhoneVerifyResult = PhoneVerifyResult;

interface TrialPhoneVerifyModalProps {
  show: boolean;
  onClose: () => void;
  onVerified: (result?: TrialPhoneVerifyResult) => void;
}

export const TrialPhoneVerifyModal = ({
  show,
  onClose,
  onVerified,
}: TrialPhoneVerifyModalProps) => {
  const { subdomain } = useContext(AccountInfoContext);
  const verify = usePhoneVerification({ subdomain, onVerified });
  const { reset } = verify;

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const submitPhone = (event: React.FormEvent) => {
    event.preventDefault();
    verify.sendCode();
  };

  const submitCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (await verify.confirmCode()) handleClose();
  };

  return (
    <InteractiveModal hide={!show} width="540px" height="auto">
      <Container>
        <Header>Verify your mobile phone</Header>
        <Subheader>
          Free trial accounts can only send SMS to their own verified mobile
          number. Add yours to begin testing.
        </Subheader>

        {verify.stage === 'phone' && (
          <Form onSubmit={submitPhone}>
            <Label htmlFor="trial-verify-phone">Mobile phone number</Label>
            <PhoneInputRow $valid={verify.phoneComplete}>
              <CountryPrefix>+1</CountryPrefix>
              <DigitsInput
                id="trial-verify-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="(555) 555-1234"
                value={formatNANP(verify.phoneDigits)}
                onChange={(e) => verify.setPhoneDigits(e.target.value)}
                required
                autoFocus
              />
            </PhoneInputRow>
            <Hint>US or Canadian mobile only. We&apos;ll text you a 6-digit code.</Hint>
            {/*
              TCPA / CTIA / Twilio TCR opt-in disclosure. Same legal
              footprint as the corresponding block in TrialSurveyModal
              Step 1 — required at every phone-collection surface, not
              just one. Includes (a) express consent, (b) carrier rates,
              (c) STOP / HELP instructions, (d) message-frequency,
              (e) Privacy + Terms links opened in a new tab so the
              modal state isn't lost.
            */}
            <TcpaDisclosure>
              By providing your phone number, you agree to receive SMS
              messages from Avochato. Msg &amp; data rates may apply. Msg
              frequency varies. Reply STOP to opt out, HELP for help.
              <br />
              <TcpaLinks>
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                <span aria-hidden> | </span>
                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
              </TcpaLinks>
            </TcpaDisclosure>
            {verify.error && <Error>{verify.error}</Error>}
            <Actions>
              <Button theme="cancel" size="medium" type="button" onClick={handleClose}>
                Cancel
              </Button>
              <Button theme="primary" size="medium" type="submit" disabled={verify.submitting || !verify.phoneComplete}>
                {verify.submitting ? 'Sending…' : 'Send code'}
              </Button>
            </Actions>
          </Form>
        )}

        {verify.stage === 'code' && (
          <Form onSubmit={submitCode}>
            <Label>
              Enter the 6-digit code sent to <PhoneNumber>{verify.phoneFormatted}</PhoneNumber>
            </Label>
            <CodeInput
              type="tel"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              placeholder="••••••"
              value={verify.code}
              onChange={(e) => verify.setCode(e.target.value)}
              required
              autoFocus
            />
            <Hint>Code expires in 10 minutes.</Hint>
            {verify.error && <Error>{verify.error}</Error>}
            <Actions>
              <Button theme="cancel" size="medium" type="button" onClick={verify.useDifferentNumber}>
                Use a different number
              </Button>
              <Button theme="primary" size="medium" type="submit" disabled={verify.submitting || verify.code.length !== 6}>
                {verify.submitting ? 'Verifying…' : 'Verify'}
              </Button>
            </Actions>
          </Form>
        )}
      </Container>
    </InteractiveModal>
  );
};

const Container = styled.div`
  background: ${colors.white};
  border-radius: 8px;
  /* Roomier padding — gives the title + form some breathing room and
     matches the proportions of the in-app verify pill we use elsewhere. */
  padding: 40px 44px 36px;
  width: 100%;
  font-family: Montserrat, sans-serif;
`;

const Header = styled.h2`
  font-size: 26px;
  /* Bolder title (was 700). Reads as the headline rather than a label. */
  font-weight: 800;
  color: ${colors.darkGreen};
  margin-top: 0;
  margin-bottom: 0;
  line-height: 1.25;
  letter-spacing: -0.2px;
`;

const Subheader = styled.p`
  font-size: 14px;
  color: ${colors.gray};
  margin-top: 14px;
  margin-bottom: 22px;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.textBlack};
  margin-bottom: 6px;
`;

const PhoneNumber = styled.span`
  color: ${colors.darkGreen};
  font-weight: 700;
`;

// Custom phone input: a fixed "+1" prefix box on the left and a 10-digit
// formatted input on the right. We only support US/CA in v1 so the country
// dropdown that ships with react-phone-input-2 is overkill (and was rendering
// a janky empty flag well that bothered us). This is plain styled inputs,
// no external lib, no asset-pipeline flag sprite to worry about.
// Note: declare props WITHOUT `$` prefix — styled-components v6's
// MakeTransient<Props> auto-adds the `$` for you (see CLAUDE.md ->
// "Styled-components v6 transient props"). Adding it here would
// produce `$$valid` and break the JSX `$valid={...}` usage.
const PhoneInputRow = styled.div<{ valid: boolean }>`
  display: flex;
  align-items: stretch;
  border: 1px solid ${({ $valid }) => ($valid ? colors.darkGreen : colors.greyer)};
  border-radius: 6px;
  overflow: hidden;
  background: ${colors.white};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${colors.darkGreen};
    box-shadow: 0 0 0 2px rgba(30, 112, 90, 0.18);
  }
`;

const CountryPrefix = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  background: ${colors.lightestGray};
  border-right: 1px solid ${colors.greyer};
  font-size: 15px;
  font-weight: 600;
  color: ${colors.textBlack};
  font-family: Montserrat, sans-serif;
  user-select: none;
`;

const DigitsInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 14px;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  color: ${colors.textBlack};
  background: ${colors.white};

  &::placeholder {
    color: ${colors.steel};
  }
`;

const CodeInput = styled.input`
  padding: 12px 16px;
  border: 1px solid ${colors.greyer};
  border-radius: 4px;
  font-size: 22px;
  letter-spacing: 8px;
  text-align: center;
  font-family: Montserrat, sans-serif;
  color: ${colors.textBlack};

  &:focus {
    border-color: ${colors.darkGreen};
    box-shadow: 0 0 0 1px ${colors.darkGreen};
    outline: none;
  }

  &::placeholder {
    color: ${colors.greyer};
    letter-spacing: 8px;
  }
`;

const Hint = styled.span`
  font-size: 12px;
  color: ${colors.steel};
  margin-top: 4px;
`;

/*
  TCPA / CTIA / Twilio TCR opt-in disclosure. Same audit-safe shape as
  the corresponding block in TrialSurveyModal — small, quiet styling
  but inline-visible on the same page as the phone field.
*/
const TcpaDisclosure = styled.div`
  font-size: 11px;
  line-height: 1.45;
  color: ${colors.steel};
  margin-top: 10px;
`;

const TcpaLinks = styled.span`
  display: inline-block;
  margin-top: 4px;

  a {
    color: ${colors.steel};
    text-decoration: underline;
    &:hover { color: ${colors.darkGreen}; }
  }

  span {
    margin: 0 4px;
    color: ${colors.greyer};
  }
`;

const Error = styled.div`
  background: ${colors.errorRed};
  color: ${colors.red};
  padding: 10px 14px;
  border-radius: 4px;
  font-size: 13px;
  margin-top: 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 28px;
`;
